import { QueryClient } from '@tanstack/react-query';
import { systemStatusKeys } from '../hooks/useSystemStatus';
import { authHistoryKeys } from '../hooks/useAuthHistory';
import { profileKeys } from '../hooks/useProfile';
import { usersKeys } from '../hooks/useUsers';
import { systemStatusService } from '../services/systemStatusService';
import { authHistoryService } from '../services/authHistoryService';
import { userService } from '../services/userService';
import { usersService } from '../services/usersService';

/**
 * Вспомогательная функция для задержки между запросами
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Предзагружает все основные данные приложения для мгновенного отображения
 * Вызывается при первой загрузке приложения после авторизации пользователя
 * Использует батчинг для предотвращения превышения rate limit
 */
export const prefetchAllData = async (queryClient: QueryClient): Promise<void> => {
  try {
    // Батч 1: Критичные данные для дашборда (выполняем сразу)
    const criticalPromises: Promise<void>[] = [];

    // 1. Профиль пользователя
    const profileCache = queryClient.getQueryData(profileKeys.profile());
    if (!profileCache) {
      criticalPromises.push(
        queryClient.prefetchQuery({
          queryKey: profileKeys.profile(),
          queryFn: () => userService.getProfile(),
          staleTime: 1000 * 60 * 5, // 5 минут
        }).then(() => {})
      );
    }

    // 2. Пользователи для дашборда (обязательная загрузка)
    const dashboardUsersCache = queryClient.getQueryData(['dashboardUsers']);
    if (!dashboardUsersCache) {
      criticalPromises.push(
        queryClient.prefetchQuery({
          queryKey: ['dashboardUsers'],
          queryFn: () => usersService.getDashboardUsers(),
          staleTime: 1000 * 30, // 30 секунд
        }).then(() => {})
      );
    }

    // 3. Статистика пользователя
    const statisticsCache = queryClient.getQueryData(profileKeys.statistics());
    if (!statisticsCache) {
      criticalPromises.push(
        queryClient.prefetchQuery({
          queryKey: profileKeys.statistics(),
          queryFn: () => userService.getStatistics(),
          staleTime: 1000 * 30, // 30 секунд
        }).then(() => {})
      );
    }

    // Выполняем критичные запросы параллельно
    await Promise.all(criticalPromises);

    // Небольшая задержка перед следующим батчем
    await delay(100);

    // Батч 2: Данные для админки (загружаем с задержкой)
    const adminPromises: Promise<void>[] = [];

    // 4. Состояние системы (для админки)
    const systemStatusCache = queryClient.getQueryData(systemStatusKeys.status());
    if (!systemStatusCache) {
      adminPromises.push(
        queryClient.prefetchQuery({
          queryKey: systemStatusKeys.status(),
          queryFn: () => systemStatusService.getStatus(),
          staleTime: 1000 * 30, // 30 секунд
        }).then(() => {})
      );
    }

    // 5. Список пользователей (для админки)
    const usersCache = queryClient.getQueryData(
      usersKeys.list({ page: 1, per_page: 25, search: '' })
    );
    if (!usersCache) {
      adminPromises.push(
        queryClient.prefetchQuery({
          queryKey: usersKeys.list({ page: 1, per_page: 25, search: '' }),
          queryFn: () => usersService.getUsers({ page: 1, per_page: 25, search: '' }),
          staleTime: 1000 * 30, // 30 секунд
        }).then(() => {})
      );
    }

    // Выполняем админские запросы параллельно
    await Promise.all(adminPromises);

    // Небольшая задержка перед следующим батчем
    await delay(100);

    // Батч 3: История авторизаций (менее критичные данные)
    const historyPromises: Promise<void>[] = [];

    // 6. История авторизаций (первая страница, для админки)
    const authHistoryCache = queryClient.getQueryData(
      authHistoryKeys.list({ page: 1, per_page: 25, search: '' })
    );
    if (!authHistoryCache) {
      historyPromises.push(
        queryClient.prefetchQuery({
          queryKey: authHistoryKeys.list({ page: 1, per_page: 25, search: '' }),
          queryFn: () => authHistoryService.getHistory({ page: 1, per_page: 25, search: '' }),
          staleTime: 1000 * 5, // 5 секунд
        }).then(() => {})
      );
    }

    // 7. История авторизаций текущего пользователя (для профиля/настроек)
    const myAuthHistoryCache = queryClient.getQueryData(
      authHistoryKeys.my({ page: 1, per_page: 25 })
    );
    if (!myAuthHistoryCache) {
      historyPromises.push(
        queryClient.prefetchQuery({
          queryKey: authHistoryKeys.my({ page: 1, per_page: 25 }),
          queryFn: () => authHistoryService.getMyHistory({ page: 1, per_page: 25 }),
          staleTime: 1000 * 5, // 5 секунд
        }).then(() => {})
      );
    }

    // Выполняем запросы истории параллельно
    await Promise.all(historyPromises);
  } catch (error) {
    // Игнорируем ошибки предзагрузки - данные загрузятся при необходимости
    console.warn('Ошибка при предзагрузке данных:', error);
  }
};
